/**
 * NutritionFoliarFieldPage — Operator foliar field workflow page.
 *
 * Routed at /nutrition/operator-sessions/:comboId.
 * Loads the combo via nutritionSessionService.getCombo(comboId)
 * (GET /nutrition/operator-sessions/:id) for status, and getMixPreview(comboId)
 * for the per-tank N·P·K mix (never PHI/intensity).
 * Three states driven by combo.status:
 *   not_started  — planned mix card + PPE checklist + Start button
 *   in_progress  — Complete form with actuals pre-filled from perTankMix × tankCount
 *   terminal     — read-only summary
 * Offline-first: startCombo/completeCombo enqueue to Dexie on network failure and
 * show a "will sync" notice; syncPendingNutritionCombos() fires when back online.
 * Weather is captured at start & completion via the reused spray weather
 * panels (parity with Protection); operator-self menu gating remains future work.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import PageHeader from '../shell/PageHeader';
import { Field, Notice } from '../patterns';
import { startError, endError } from './nutritionSessionTimes';
import {
  nutritionSessionService,
  NutritionMixPreview,
  NutritionCombo,
} from '../../services/nutritionSessionService';
import {
  syncPendingNutritionCombos, readPendingFoliarComboCount,
} from '../../services/nutritionOperatorSessionSyncService';
import {
  elapsedMinutes, stepTanks, resolveActuals, derivedReadbackLines, MIN_TANKS,
} from './foliarOperator';
import { formatMeasure } from '../../utils/formatMeasure';
import NutritionMixPreviewCard from './NutritionMixPreviewCard';
import FoliarSyncBanner from './FoliarSyncBanner';
import OpenSignOffWeatherPanel, { SprayWeatherSnapshotInput } from '../spray/OpenSignOffWeatherPanel';
import CloseSignOffWeatherPanel, { CloseSignOffWeatherSnapshot } from '../spray/CloseSignOffWeatherPanel';
import { db } from '../../db';
import type { ComboWeatherSnapshot, NutritionMyWorkItem } from '../../db';
import { describeWeather } from './weatherReadback';
import './nutrition.css';

// ── PPE checklist items (minimal — Slice 4 can expand from programme config) ──

const PPE_ITEMS = [
  'Gloves',
  'Eye protection',
  'Protective overalls',
];

// ── Timestamp helpers ──────────────────────────────────────────────────────────

function nowAsDateTimeLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function dateTimeLocalToIso(value: string): string {
  if (!value) return new Date().toISOString();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

// ── Weather snapshot converters (flat combo shape) ────────────────────────────
// Copied from OperatorComboWorkFlow.tsx:168-222 (Protection's equivalent
// conversion) so the two reused spray weather panels' onChange shapes map to
// ComboWeatherSnapshot without modifying the panels themselves.

function openWeatherToCombo(snapshot: SprayWeatherSnapshotInput | null): ComboWeatherSnapshot | undefined {
  if (!snapshot) return undefined;
  const { temperature, wind, precipitation } = snapshot.finalSelected;
  if (!temperature || !wind || !precipitation) return undefined;
  const out: ComboWeatherSnapshot = {
    temperatureCategory: temperature,
    windCategory: wind,
    precipitationCategory: precipitation,
  };
  if (snapshot.systemReported) {
    out.systemReported = {
      temp: snapshot.systemReported.temp,
      windSpeed: snapshot.systemReported.windSpeed,
      precipitation: snapshot.systemReported.precipitation,
    };
  }
  return out;
}

function closeWeatherToCombo(snapshot: CloseSignOffWeatherSnapshot | null): ComboWeatherSnapshot | undefined {
  if (!snapshot) return undefined;
  if (!snapshot.temperatureCategory || !snapshot.windCategory || !snapshot.precipitationCategory) {
    return undefined;
  }
  const out: ComboWeatherSnapshot = {
    temperatureCategory: snapshot.temperatureCategory,
    windCategory: snapshot.windCategory,
    precipitationCategory: snapshot.precipitationCategory,
  };
  if (snapshot.systemReported) {
    const sr = snapshot.systemReported;
    if (
      typeof sr.temp === 'number' &&
      typeof sr.windSpeed === 'number' &&
      typeof sr.precipitation === 'number'
    ) {
      out.systemReported = { temp: sr.temp, windSpeed: sr.windSpeed, precipitation: sr.precipitation };
    }
  }
  if (snapshot.overrideComment) out.overrideComment = snapshot.overrideComment;
  return out;
}

// SSR-safe online check — treat unknown as online (so weather is required).
function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

// ── Terminal statuses ─────────────────────────────────────────────────────────

const TERMINAL_STATUSES: NutritionCombo['status'][] = ['completed', 'incomplete', 'cancelled'];

// ── Component ─────────────────────────────────────────────────────────────────

const NutritionFoliarFieldPage: React.FC = () => {
  const { t } = useTranslation();
  const { comboId } = useParams<{ comboId: string }>();
  const navigate = useNavigate();

  // ── Cached worklist identity (header title: round · orchard) ──────────────
  // Read-only lookup of the SAME row the worklist page (NutritionMySessionsPage)
  // caches, keyed by this combo's id. It can legitimately be absent — an
  // operator arriving by QR scan or a cold deep link has no cached worklist —
  // so the header falls back to the generic title rather than rendering blank.
  const workItem = useLiveQuery(
    () => (comboId ? db.nutritionMySessions.get(comboId) : undefined),
    [comboId],
  ) as NutritionMyWorkItem | undefined;

  // ── Data state ─────────────────────────────────────────────────────────────
  const [combo, setCombo] = useState<NutritionCombo | null>(null);
  const [mixPreview, setMixPreview] = useState<NutritionMixPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // ── Form state ─────────────────────────────────────────────────────────────
  const [ppeChecks, setPpeChecks] = useState<Record<string, boolean>>({});
  const [startedAt, setStartedAt] = useState<string>(nowAsDateTimeLocal());
  const [endedAt, setEndedAt] = useState<string>(nowAsDateTimeLocal());
  // Named `tanks` (not `tankCount`): Task 10's in-progress rebuild consumes
  // this state, plus the `tanksNum` const below, directly.
  const [tanks, setTanks] = useState<string>('');
  const [openWeather, setOpenWeather] = useState<SprayWeatherSnapshotInput | null>(null);
  const [closeWeather, setCloseWeather] = useState<CloseSignOffWeatherSnapshot | null>(null);

  // ── Submit / offline state ─────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [pendingSync, setPendingSync] = useState(false);

  // ── Offline sync banner state (Compact Mode) ──────────────────────────────
  // Same shape as Task 7's worklist page (NutritionMySessionsPage), so the
  // operator's two screens agree.
  const [online, setOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const [pendingComboCount, setPendingComboCount] = useState(0);

  // ── Derived render state from combo status ────────────────────────────────
  // Hoisted above the elapsed-clock effect (and the loading/error early
  // returns further down): hooks must run unconditionally on every render, so
  // anything a hook's deps rely on has to be computed before it.
  const comboStatus = combo?.status ?? null;
  const isNotStarted = comboStatus === 'not_started';
  const isInProgress = comboStatus === 'in_progress';
  const isTerminal = comboStatus !== null && TERMINAL_STATUSES.includes(comboStatus);

  // ── Elapsed-time clock (Compact Mode) ──────────────────────────────────────
  // 60s, not 1s (U5's ruling): a per-second clock is a re-render per second on
  // a battery-constrained field device.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!isInProgress) return undefined;
    // Seed a fresh reading the instant we transition into in_progress — `now`
    // may otherwise still hold its mount-time value (stale by however long the
    // operator spent on PPE/weather before pressing Start), which would show
    // "Running 0 minutes" for up to the first 60s of a genuinely-running combo.
    setNow(new Date());
    const h = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(h);
  }, [isInProgress]);
  const minutes = elapsedMinutes(combo?.startedAt ?? null, now);

  // The single tanks-quantity const for the APPLIED figure (Interfaces: "the
  // single `tanksNum` const") — computed ONCE here and consumed at every call
  // site that submits or displays what THIS OPERATOR is actually booking
  // (handleComplete, the in-progress branch's hero/read-back/stepper) so a
  // consumption quantity can never diverge across them. Optional-chained:
  // mixPreview is still null on the renders before loadData resolves, and
  // this is computed unconditionally (above the loading/error early
  // returns) like every other hook-adjacent const.
  /*
   * NO fallback to the preview (#506, D3).
   *
   * `tanksExact` is the WHOLE SESSION's tank fraction. While this const fell
   * back to it, a blank box still submitted the whole session's work as this
   * operator's portion — so making the seed conditional below would have
   * changed nothing at all. Consumption is now derived server-side from
   * `tankCount x dosePerTank`, which makes this the single load-bearing input
   * on the screen: it must be an observation, never a default that survived.
   */
  const tanksNum = Number(tanks) || 0;

  /*
   * The PLANNED figure — deliberately a SEPARATE const from `tanksNum`
   * (Ruling R13, task-8 review). `tanksNum` has no preview fallback on
   * purpose: it is what this operator is applying, and must never silently
   * default to the whole session's fraction. But the not-started hero below
   * is captioned "tanks planned" — before this operator has entered
   * anything of their own, it is showing the SESSION's plan, which the
   * whole-session fraction genuinely IS. Reading `tanksNum` there (0 when
   * the seed correctly doesn't fire, e.g. a multi-operator session) would
   * render "0 tanks planned", which is wrong in the other direction — the
   * plan did not vanish because this operator hasn't stated their portion
   * yet. This is the ONLY consumer of the whole-session fraction on this
   * page now; everything that submits or books work reads `tanksNum`.
   */
  const plannedTanksNum = mixPreview?.tanksExact || mixPreview?.tankCount || 0;

  /*
   * Display only (#506, D1). The server recomputes `productsConsumed` from
   * `tankCount x dosePerTank` and DISCARDS whatever the client sends — spray's
   * rule (operatorSpraySessionService :84/:158) — so this is the read-back that
   * tells the operator what their tank count will book, not an input to it.
   *
   * The per-product override editor that used to sit beneath it is GONE, and
   * that is a deliberate reversal of F5 spec 2.7 (which rejected a tanks-only
   * screen so a zero capture — "we ran out of manganese" — stayed expressible),
   * decided in the P2a2 spec §4.1. Do not restore it as a regression fix: a
   * control whose value the server ignores is worse than no control.
   */
  const actuals = resolveActuals(mixPreview?.perTankMix ?? [], tanksNum, {});

  // ── Load combo + mix preview ───────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!comboId) return;
    setLoadError('');
    try {
      // Load both in parallel: mix preview (N·P·K per-tank) + combo status.
      // getCombo uses GET /nutrition/operator-sessions/:id (added to service in Slice 3).
      const [preview, comboDoc] = await Promise.all([
        nutritionSessionService.getMixPreview(comboId),
        nutritionSessionService.getCombo(comboId),
      ]);
      setMixPreview(preview);
      setCombo(comboDoc);

      /*
       * Pre-fill with the EXACT tank fraction — but ONLY when this operator is
       * the session's only one (#506, D3).
       *
       * A part tank's balance is sprayed on the NEXT orchard, so this orchard
       * uses its fraction — 0.4 of a tank, not 1. Since `dosePerTank` is a
       * FULL tank's worth, prefilling the ceiling would compute consumption as
       * a whole tank and over-state every small orchard (M1: 90 kg against a
       * planned 36 kg).
       *
       * But `tanksExact` describes the WHOLE SESSION. A foliar session may be
       * worked by several operators, each doing a PORTION, so pre-filling it
       * for two operators has each of them claim the entire job — live
       * evidence: session M4's two combos each recorded 0.55 (1650 L / 3000 L,
       * the whole fraction), while M5's operators typed 0.18 and 0.15, summing
       * to exactly 990/3000. With siblings present the operator must state
       * their own figure, and submit stays disabled until they do.
       *
       * An ABSENT activeComboCount (a preview cached before #506 shipped) is
       * treated as unknown, not as 1 — the safe reading is to ask.
       */
      if (preview.activeComboCount === 1) {
        setTanks((prev) => prev || String(preview.tanksExact || preview.tankCount));
      }
    } catch (err: any) {
      /*
       * The backend returns 422 `plan-unavailable` when the session's programme
       * is gone AND there is no persisted plan to rebuild from. That used to be
       * a mix card full of zeroes (#463); it must not now become a generic
       * "could not load", which sends the operator looking for a network fault
       * that does not exist. Name the actual condition.
       */
      const code = err?.response?.data?.error;
      setLoadError(code === 'plan-unavailable'
        ? t('nutrition.sessions.field.planUnavailable')
        : t('nutrition.sessions.field.loadError'));
    } finally {
      setLoading(false);
    }
  }, [comboId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Offline sync banner: pending-action count + live connectivity ─────────
  // The .catch lives IN HERE (not at each call site) so all three callers —
  // the mount effect and both handlers' `finally` blocks — are guarded
  // identically; a Dexie read failure (e.g. private-mode storage) must never
  // become an unhandled rejection regardless of which caller triggered it.
  const loadPendingCount = useCallback(async () => {
    try {
      const count = await readPendingFoliarComboCount();
      setPendingComboCount(count);
    } catch {
      // Best-effort: the banner simply won't update this cycle.
    }
  }, []);

  useEffect(() => {
    loadPendingCount();
  }, [loadPendingCount]);

  useEffect(() => {
    // The banner is the operator's only signal that work is being held
    // locally, so it must track connectivity live rather than only at mount.
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // ── Handle Start ──────────────────────────────────────────────────────────
  const handleStart = async () => {
    if (!comboId) return;
    // Recompute with a fresh 'now' at submit time (catches elapsed time)
    if (startError({ startedAt, now: new Date() })) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      await nutritionSessionService.startCombo(comboId, {
        startedAt: dateTimeLocalToIso(startedAt),
        weatherStart: openWeatherToCombo(openWeather) ?? ({} as ComboWeatherSnapshot),
      });

      if (typeof navigator !== 'undefined' && navigator.onLine) {
        syncPendingNutritionCombos().catch(() => {});
        // Reload to pick up server state
        await loadData();
      } else {
        // Offline: enqueue has happened inside startCombo; show "will sync" notice
        setPendingSync(true);
        // Optimistically flip to in_progress so the page renders the complete form
        setCombo((prev) =>
          prev ? { ...prev, status: 'in_progress' } : null,
        );
      }
    } catch (err: any) {
      setSubmitError(err?.message ?? t('nutrition.sessions.field.startError'));
    } finally {
      setSubmitting(false);
      // Offline start enqueues into the Dexie queue; refresh the banner count
      // whatever the outcome (a no-op when the submission went straight
      // through online). loadPendingCount is internally guarded — see its
      // definition — so no .catch is needed at any call site.
      loadPendingCount();
    }
  };

  // ── Handle Complete ───────────────────────────────────────────────────────
  const handleComplete = async () => {
    if (!comboId || !mixPreview) return;
    // Recompute with a fresh 'now' at submit time (catches elapsed time).
    // Use persisted combo.startedAt for the end-before-start check; fall back
    // to the local startedAt state only if the server hasn't persisted one yet.
    const persistedStart = combo?.startedAt ?? dateTimeLocalToIso(startedAt);
    if (endError({ startedAt: persistedStart, endedAt, now: new Date() })) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      await nutritionSessionService.completeCombo(comboId, {
        endedAt: dateTimeLocalToIso(endedAt),
        // The WIRE name stays `tankCount` (Ruling 1). Renaming it would break
        // every completion already queued in db.nutritionComboCompletes: the
        // field would be absent on drain. Since #506 that is no longer a
        // silent plan-booking — a multi-combo session rejects it with 400
        // TANK_COUNT_REQUIRED — but a dead queue is still a dead queue.
        //
        // No `productsConsumed`: the server discards client quantities (D1).
        tankCount: tanksNum,
        weatherEnd: closeWeatherToCombo(closeWeather) ?? ({} as ComboWeatherSnapshot),
      });

      if (typeof navigator !== 'undefined' && navigator.onLine) {
        syncPendingNutritionCombos().catch(() => {});
        await loadData();
      } else {
        setPendingSync(true);
        setCombo((prev) =>
          prev ? { ...prev, status: 'completed' } : null,
        );
      }
    } catch (err: any) {
      setSubmitError(err?.message ?? t('nutrition.sessions.field.completeError'));
    } finally {
      setSubmitting(false);
      loadPendingCount();
    }
  };

  // ── Inline time validation (recomputed on every render with current clock) ───
  const renderNow = new Date();
  const startErr = startError({ startedAt, now: renderNow });
  // For the end check, prefer the persisted combo.startedAt (available once the
  // combo is in_progress), falling back to the local startedAt state.
  const persistedStartForEnd = combo?.startedAt ?? dateTimeLocalToIso(startedAt);
  const endErr = endError({ startedAt: persistedStartForEnd, endedAt, now: renderNow });

  const allPpeChecked = PPE_ITEMS.every((item) => ppeChecks[item] === true);

  const weatherStartComplete = Boolean(openWeatherToCombo(openWeather));
  const weatherEndComplete = Boolean(closeWeatherToCombo(closeWeather));

  // ── Render: loading ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="ntr-page ntr-page--inset" data-testid="foliar-field-loading">
        <p className="ntr-muted">{t('common.loading')}</p>
      </div>
    );
  }

  // ── Render: load error ────────────────────────────────────────────────────
  if (loadError || !mixPreview) {
    return (
      <div className="ntr-page ntr-page--inset" data-testid="foliar-field-error">
        <Notice tone="danger">
          {loadError || t('nutrition.sessions.field.loadError')}
        </Notice>
      </div>
    );
  }

  // ── Common header ─────────────────────────────────────────────────────────
  // Title: round · orchard from the cached worklist row when we have one
  // (the common case — the operator arrived from My Sessions); falls back to
  // the generic title on a cold cache (QR scan / cold deep link), rather than
  // rendering a blank header.
  // Elapsed reading appended to the meta line only while in_progress — the
  // header is shared across all three branches, but the clock is only
  // meaningful once a combo has started.
  const header = (
    <PageHeader
      title={
        workItem
          ? `${workItem.roundName} · ${workItem.orchardOrGroup}`
          : t('nutrition.sessions.field.title')
      }
      meta={
        isInProgress && minutes !== null ? (
          <>
            {mixPreview.machine.name}
            <span className="ntr-fol-cap-elapsed" data-testid="foliar-elapsed">
              {' · '}
              {t('nutrition.sessions.field.elapsed', { count: minutes })}
            </span>
          </>
        ) : (
          mixPreview.machine.name
        )
      }
      actions={
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          data-testid="foliar-back-btn"
          // Ruling 14: the field page's Back action targets the operator's
          // worklist, not the office session list.
          onClick={() => navigate('/nutrition/my-sessions')}
        >
          {t('common.back')}
        </button>
      }
    />
  );

  // ── Offline sync banner (Compact Mode) ────────────────────────────────────
  // Shared component with Task 7's worklist page (NutritionMySessionsPage),
  // not a copy — see FoliarSyncBanner.tsx. Distinct from `syncNotice` below:
  // this banner reports "N actions are waiting"; syncNotice reports "this
  // submission is queued" — the two say different things and neither
  // replaces the other.
  const syncBanner = (
    <FoliarSyncBanner online={online} pendingCount={pendingComboCount} className="mb-3" />
  );

  // ── Offline pending-sync notice ───────────────────────────────────────────
  const syncNotice = pendingSync && (
    <Notice
      tone="info"
      className="mb-3"
      testId="foliar-queued-notice"
    >
      {t('nutrition.sessions.field.queuedNotice')}
    </Notice>
  );

  // ── Submit error notice ───────────────────────────────────────────────────
  const errorNotice = submitError && (
    <Notice tone="danger" className="mb-3">
      {submitError}
    </Notice>
  );

  // ─────────────────────────────────────────────────────────────────────────
  //  not_started: tanks hero + planned mix + PPE checklist + start datetime
  //  + Start button (Compact Mode order — spec §4.7)
  // ─────────────────────────────────────────────────────────────────────────
  if (isNotStarted && !pendingSync) {
    return (
      <div className="ntr-page ntr-page--inset ntr-fol-cap-page" data-testid="foliar-field-not-started">
        {header}

        {syncBanner}
        {errorNotice}

        {/* Tanks hero — the number the operator cares about first */}
        <div className="ntr-card">
          <div className="ntr-card__body">
            <div className="ntr-fol-hero" data-testid="foliar-tanks-value">
              <span className="ntr-fol-hero__value">{formatMeasure(plannedTanksNum)}</span>
              {/* Fix 3 (whole-branch review, human ruling): this hero is the
                  EXACT fraction (tanksExact || tankCount) — "tanks planned" —
                  distinct from the worklist's hero, which is the ceiling tank
                  count ("tanks to mix"). Both numbers are genuinely different
                  farm facts (a tank is always a full tank; a part tank's
                  balance is sprayed on the next orchard) and are kept as-is;
                  only the caption differentiates them.
                  Reads `plannedTanksNum`, NOT `tanksNum` (Ruling R13, #506):
                  this hero shows the SESSION's plan before this operator has
                  entered anything, unconditionally, regardless of
                  activeComboCount — unlike `tanksNum`, which never falls back
                  to the preview and stays 0 until this operator states their
                  own portion. */}
              <span className="ntr-fol-hero__unit">{t('nutrition.sessions.field.tanksPlannedHero')}</span>
            </div>
            <div className="ntr-fol-cap-tanks-sub" data-testid="foliar-tanks-sub">
              {t('nutrition.sessions.field.tanksEach', {
                vol: formatMeasure(mixPreview.machine.tankVolume, 'L'),
              })}
              {' · '}
              {t('nutrition.sessions.field.tanksTotal', {
                vol: formatMeasure(mixPreview.totalWaterL, 'L'),
              })}
            </div>
          </div>
        </div>

        {/* Load per tank — planned N·P·K mix preview. A heading of our own,
            not NutritionMixPreviewCard's internal title: that card is shared
            with the manager's session-detail page, so its own heading must
            not change. */}
        <div className="ntr-section-label" data-testid="foliar-load-per-tank-title">
          {t('nutrition.sessions.field.loadPerTankTitle')}
        </div>
        <NutritionMixPreviewCard preview={mixPreview} />

        {/* PPE checklist — full-width, glove-operable tap targets */}
        <div className="ntr-card">
          <div className="ntr-card__header">
            <h3 className="ntr-card__title">
              {t('nutrition.sessions.field.ppeTitle')}
            </h3>
          </div>
          <div className="ntr-card__body">
            <p className="ntr-muted mb-3" style={{ fontSize: 13 }}>
              {t('nutrition.sessions.field.ppeIntro')}
            </p>
            <div className="d-flex flex-column gap-2">
              {PPE_ITEMS.map((item) => (
                <label
                  key={item}
                  htmlFor={`ppe-${item}`}
                  className="ntr-fol-ppe__item"
                >
                  <input
                    id={`ppe-${item}`}
                    type="checkbox"
                    checked={ppeChecks[item] === true}
                    onChange={(e) =>
                      setPpeChecks((prev) => ({ ...prev, [item]: e.target.checked }))
                    }
                    data-testid={`ppe-check-${item.replace(/\s+/g, '-').toLowerCase()}`}
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
            {!allPpeChecked && (
              <p className="ntr-error mt-2" style={{ fontSize: 12 }}>
                {t('nutrition.sessions.field.ppeRequired')}
              </p>
            )}
          </div>
        </div>

        {/* Start datetime */}
        <div className="ntr-card">
          <div className="ntr-card__header">
            <h3 className="ntr-card__title">
              {t('nutrition.sessions.field.startTimeTitle')}
            </h3>
          </div>
          <div className="ntr-card__body">
            <Field
              label={t('nutrition.sessions.field.startTimeLabel')}
              controlId="foliar-start-time"
              errorText={
                startErr
                  ? t('nutrition.sessions.field.error.startInFuture')
                  : undefined
              }
            >
              <input
                id="foliar-start-time"
                type="datetime-local"
                className="pat-input"
                value={startedAt}
                max={nowAsDateTimeLocal()}
                onChange={(e) => setStartedAt(e.target.value)}
                data-testid="foliar-start-time-input"
              />
            </Field>
            <OpenSignOffWeatherPanel
              onChange={setOpenWeather}
              sessionId={comboId}
              startTime={startedAt}
              compact
            />
          </div>
        </div>

        <div className="ntr-actions">
          <button
            type="button"
            className="pat-btn pat-btn--primary w-100"
            onClick={handleStart}
            disabled={submitting || !allPpeChecked || !!startErr || (isOnline() && !weatherStartComplete)}
            data-testid="foliar-start-btn"
            aria-label={t('nutrition.sessions.field.startBtn')}
          >
            {submitting
              ? t('nutrition.sessions.field.starting')
              : t('nutrition.sessions.field.startBtn')}
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  in_progress (or optimistic after offline start): Complete form
  // ─────────────────────────────────────────────────────────────────────────
  if (isInProgress || (pendingSync && !isTerminal)) {
    return (
      <div className="ntr-page ntr-page--inset ntr-fol-cap-page" data-testid="foliar-field-in-progress">
        {header}

        {syncBanner}
        {syncNotice}
        {errorNotice}

        {/* N·P·K mix (shows per-tank planned quantities; operator can adjust tank count) */}
        <NutritionMixPreviewCard preview={mixPreview} />

        {/* Complete form */}
        <div className="ntr-card">
          <div className="ntr-card__header">
            <h3 className="ntr-card__title">
              {t('nutrition.sessions.field.completeTitle')}
            </h3>
          </div>
          <div className="ntr-card__body">
            {/* Tanks applied — the hero number, adjusted with a ± stepper.
                `.ntr-stepper` is reused VERBATIM (Ruling 6) — see
                SoilGroupFieldPage.tsx:279, its only other consumer — with a
                sibling `.ntr-fol-stepper` modifier for any foliar-specific
                metric, never an edit to the shared class itself. A visible
                <label htmlFor> (review round 1, Important 1) — the caption
                had regressed to an aria-label only, and a label is markup
                the CSS-only next task cannot restore. */}
            <label className="ntr-meta-grid__label" htmlFor="foliar-tanks">
              {t('nutrition.sessions.field.tankCountLabel')}
            </label>
            <div className="ntr-stepper ntr-fol-stepper">
              <button
                type="button"
                data-testid="foliar-tanks-down"
                aria-label={t('nutrition.sessions.field.tanksDown')}
                onClick={() => setTanks((v) => stepTanks(v || String(tanksNum), -0.1))}
              >
                −
              </button>
              <input
                id="foliar-tanks"
                data-testid="foliar-tanks-input"
                type="number"
                min={MIN_TANKS}
                /* 0.01 not 0.1: a real fraction like 0.55 tanks must be enterable. */
                step={0.01}
                className="pat-input"
                value={tanks}
                onChange={(e) => setTanks(e.target.value)}
                aria-label={t('nutrition.sessions.field.tankCountLabel')}
              />
              <button
                type="button"
                data-testid="foliar-tanks-up"
                aria-label={t('nutrition.sessions.field.tanksUp')}
                onClick={() => setTanks((v) => stepTanks(v || String(tanksNum), 0.1))}
              >
                +
              </button>
            </div>

            {/* Section label for the products area (review round 1,
                Important 1) — reuses the `actualsTitle` key, otherwise an
                orphan in both locale trees. */}
            <div className="ntr-section-label" data-testid="foliar-actuals-title">
              {t('nutrition.sessions.field.actualsTitle')}
            </div>

            {/* Derived read-back — every product at dosePerTank × tanks. */}
            <div className="ntr-fol-readback" data-testid="foliar-readback">
              {derivedReadbackLines(actuals).join(' · ')}
            </div>

            {/*
             * The per-product override editor that used to sit here (Task 10:
             * an Adjust-individual-products disclosure, one input per line, a
             * Reset button) is REMOVED — a deliberate reversal of F5 spec 2.7,
             * decided in the P2a2 spec §4.1 (#506, D1).
             *
             * F5 built that editor on purpose: spec 2.7 rejected a tanks-only
             * screen specifically so an override of 0 stayed expressible — a
             * real capture ("we ran out of manganese"), not a missing value.
             * It is going now because the server no longer listens: D1 makes
             * it recompute productsConsumed itself from
             * `tankCount x dosePerTank` and discard whatever the client sent,
             * so the editor's value was silently doing nothing — and a control
             * whose value is ignored is worse than no control. Do not restore
             * it as a regression fix without first reopening D1.
             */}

            {/* End datetime */}
            <div style={{ marginTop: 16 }}>
              <Field
                label={t('nutrition.sessions.field.endTimeLabel')}
                controlId="foliar-end-time"
                errorText={
                  endErr === 'endInFuture'
                    ? t('nutrition.sessions.field.error.endInFuture')
                    : endErr === 'endBeforeStart'
                    ? t('nutrition.sessions.field.error.endBeforeStart')
                    : undefined
                }
              >
                <input
                  id="foliar-end-time"
                  type="datetime-local"
                  className="pat-input"
                  value={endedAt}
                  max={nowAsDateTimeLocal()}
                  onChange={(e) => setEndedAt(e.target.value)}
                  data-testid="foliar-end-time-input"
                />
              </Field>
              <CloseSignOffWeatherPanel
                compact
                scopeId={comboId!}
                endTime={endedAt}
                onChange={setCloseWeather}
              />
            </div>

            <div className="ntr-actions">
              <button
                type="button"
                className="pat-btn pat-btn--primary"
                onClick={handleComplete}
                disabled={submitting || tanksNum <= 0 || !!endErr || (isOnline() && !weatherEndComplete)}
                data-testid="foliar-complete-btn"
                aria-label={t('nutrition.sessions.field.completeBtn')}
              >
                {submitting
                  ? t('nutrition.sessions.field.completing')
                  : t('nutrition.sessions.field.completeBtn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  terminal: read-only summary
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="ntr-page ntr-page--inset ntr-fol-cap-page" data-testid="foliar-terminal-view">
      {header}

      {syncBanner}
      {syncNotice}

      <div className="ntr-card">
        <div className="ntr-card__header">
          <h3 className="ntr-card__title">
            {t('nutrition.sessions.field.terminalTitle')}
          </h3>
        </div>
        <div className="ntr-card__body">
          <div className="ntr-meta-grid">
            <div className="ntr-meta-grid__item">
              <span className="ntr-meta-grid__label">
                {t('nutrition.sessions.list.columns.status')}
              </span>
              <span className="ntr-meta-grid__value">
                {comboStatus
                  ? t('nutrition.sessions.field.status.' + comboStatus, comboStatus)
                  : '—'}
              </span>
            </div>
          </div>
          {(combo?.weatherSnapshots?.start || combo?.weatherSnapshots?.end) && (
            <div className="ntr-meta-grid ntr-fol-cap-terminal-weather" data-testid="foliar-terminal-weather">
              <div className="ntr-meta-grid__item">
                <span className="ntr-meta-grid__label">
                  {t('nutrition.sessions.field.weatherStart', 'Weather at start')}
                </span>
                <span className="ntr-meta-grid__value">
                  {describeWeather(combo?.weatherSnapshots?.start)}
                </span>
              </div>
              <div className="ntr-meta-grid__item">
                <span className="ntr-meta-grid__label">
                  {t('nutrition.sessions.field.weatherEnd', 'Weather at completion')}
                </span>
                <span className="ntr-meta-grid__value">
                  {describeWeather(combo?.weatherSnapshots?.end)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <NutritionMixPreviewCard preview={mixPreview} />
    </div>
  );
};

export default NutritionFoliarFieldPage;
